import { IconFont, Input, itemBackgroundTransitionOnClick, Popover, SelectDropdown, Toast } from '@byecode/ui'
import type { AnyObject, DataSourceAbstract, RectSize, RectSizeUnit, VariableADTvalue } from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import { emptyNumberField, getBlockChildren, getInputInnerTypeMatch, VariableSelect, type VariableTree2 } from '@lighthouse/shared'
import isDeepEqual from 'fast-deep-equal'
import { useAtomValue } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import React, { useCallback, useMemo, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'
import { useImmer } from 'use-immer'

import { pageBlockContextIndexAtom, selectedBlockContextAtom } from '@/atoms/page/state'
import type { NodeIdWithScope } from '@/atoms/page/types'
import { useDeviceType } from '@/hooks/useApplication'
import { transform2Integer, transform2Number } from '@/utils/number'

import { SizeIcon } from './SizeIcon'
import { convertSize, getSizeBaseType, type SizeInputType } from './utils'

export type SizeInputValue = {
    size?: RectSize
    unit?: RectSizeUnit
    variable?: VariableADTvalue
}

export interface VariableOptions {
    viewOptions?: VariableTree2[]
    pageOptions?: VariableTree2[]
    userOption?: VariableTree2
    dataSourceList?: DataSourceAbstract[]
}

interface SizeInputProps {
    type: SizeInputType
    value?: SizeInputValue
    onChange: (value: SizeInputValue) => void
    disableAutoOption?: boolean
    disableFillOption?: boolean
    disabled?: boolean
    deps?: AnyObject
    variableOptions?: VariableOptions
}

type RectSizeOption = RectSize | RectSizeUnit

const SCxRectSizeInputWrapper = styled.div`
    display: flex;
    width: 180px;
`

const SCxVariableSelectWrapper = styled.div`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
`

const SCxDropdownButton = styled.div`
    display: flex;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    background-color: var(--color-gray-100);
    border-left: 1px solid var(--color-gray-200);
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
    cursor: pointer;

    ${itemBackgroundTransitionOnClick}

    &:hover {
        background-color: var(--color-gray-200);
    }
`

const rectSizeValue: Record<string, 'auto' | 'fill'> = {
    适应内容: 'auto',
    撑满容器: 'fill'
}

const cssUnit = new Set(['px', '%', 'vw', 'vh'])

export const RectSizeInput: React.FC<SizeInputProps> = ({
    type,
    value,
    onChange,
    disableAutoOption,
    disableFillOption,
    disabled,
    deps,
    variableOptions
}) => {
    const previewType = useDeviceType()
    const [dropdownOpened, setDropdownOpened] = useState(false)
    // 拖拽是否从 auto/fill 起始：若是，拖拽期间保持 readOnly 分支不切换，避免 SizeIcon 卸载导致拖拽中断
    const [dragStartedReadOnly, setDragStartedReadOnly] = useState(false)
    const init = useMemo(() => value ?? { size: undefined, unit: undefined }, [value])
    const [v, setV] = useImmer<SizeInputValue>(init)

    const lowerCaseType = type.toLowerCase()
    const inputIconType = useMemo(() => {
        if (lowerCaseType.includes('width')) {
            return 'Width'
        }
        if (lowerCaseType.includes('height')) {
            return 'Height'
        }
    }, [lowerCaseType])

    const selectedContext = useAtomValue(selectedBlockContextAtom)
    const blockIdWithScope = useMemo<NodeIdWithScope | undefined>(() => {
        if (!selectedContext?.block) {
            return undefined
        }
        return { id: selectedContext.block.id, scope: selectedContext.scope }
    }, [selectedContext])

    useUpdateEffect(() => {
        if (init?.size === v.size && init?.unit === v.unit && isDeepEqual(init?.variable, v.variable)) {
            return
        }
        setV(init)
    }, [deps, init.size, init.unit, init.variable])

    const label = useMemo(() => {
        if (inputIconType === 'Width') {
            return '宽'
        }
        return '高'
    }, [inputIconType])

    const [fixedIcon, autoIcon, fillIcon] = useMemo(() => {
        if (inputIconType === 'Width') {
            return ['FixedW', 'HugW', 'FillW']
        }
        return ['FixedH', 'HugH', 'FillH']
    }, [inputIconType])

    const dropdownOptions = useMemo(() => {
        return [
            { label: `固定${label}度`, value: 'px', icon: fixedIcon },
            { label: `百分比${label}`, value: '%', icon: 'PropertyPercent' },
            { label: '相对视窗宽度', value: 'vw', icon: 'ViewW' },
            { label: '相对视窗高度', value: 'vh', icon: 'ViewH' },
            { label: '适应内容', value: 'auto', icon: autoIcon },
            { label: '撑满容器', value: 'fill', icon: fillIcon },
            { label: `相对根元素${label}`, value: 'rem', icon: 'Rem' }
        ].filter(({ value }) => {
            if (disableAutoOption && value === 'auto') {
                return false
            }
            if (disableFillOption && value === 'fill') {
                return false
            }
            const t = getSizeBaseType(type)
            if (t === 'width') {
                return value !== 'vh'
            }
            if (t === 'height') {
                return value !== 'vw'
            }
            return true
        })
    }, [autoIcon, disableAutoOption, disableFillOption, fillIcon, fixedIcon, label, type])

    const isReadOnly = v?.size === 'auto' || v?.size === 'fill'

    // 将旧格式（size: number）或 VALUE 变量统一转为 VariableADTvalue 供 VariableSelect 展示
    const variableValue = useMemo((): VariableADTvalue | undefined => {
        if (v.variable) return v.variable
        if (typeof v.size === 'number') {
            return {
                type: VariableType.VALUE,
                valueVariable: { type: 'number', value: String(v.size) }
            }
        }
        return undefined
    }, [v.variable, v.size])

    const handleVariableChange = useCallback(
        (variable: VariableADTvalue) => {
            const val: SizeInputValue = {
                size: undefined,
                unit: value?.unit || 'px',
                variable
            }
            setV(val)
            if (isDeepEqual(value, val)) {
                return
            }
            onChange(val)
        },
        [onChange, setV, value]
    )

    // 当前数值（用于 SizeIcon 拖拽起点回退与显示）
    const currentSizeNumber = useMemo(() => {
        if (v.variable?.type === VariableType.VALUE) {
            const raw = v.variable.valueVariable?.value
            const num = transform2Number(Number(raw).toFixed(2))
            return Number.isNaN(num) ? undefined : num
        }
        return typeof v.size === 'number' ? v.size : undefined
    }, [v.variable, v.size])

    // px/%/vw/vh：以当前数值为起点按 px 偏移拖拽（不传 blockStyleSize）；
    // rem/无单位：以 DOM px 为起点拖拽（传 blockStyleSize，由 handleMoveChange 回退为 px）
    const sizeIconBlockStyleSize = useMemo<{ blockId: NodeIdWithScope | undefined; sizeInputType: SizeInputType } | undefined>(() => {
        if (v?.unit && cssUnit.has(v.unit)) {
            return undefined
        }
        return { blockId: blockIdWithScope, sizeInputType: type }
    }, [v?.unit, blockIdWithScope, type])

    // 拖拽产生的数值 → 转为 VALUE 变量上报；px/%/vw/vh 保留当前单位，其余（rem/无单位）回退为 px
    const handleMoveChange = useCallback(
        (num: number) => {
            const rounded = transform2Integer(num)
            const unit: RectSizeUnit = value?.unit && cssUnit.has(value.unit) ? value.unit : 'px'
            const variable: VariableADTvalue = {
                type: VariableType.VALUE,
                valueVariable: { type: 'number', value: String(rounded) }
            }
            const val: SizeInputValue = { size: undefined, unit, variable }
            setV(val)
            if (isDeepEqual(value, val)) {
                return
            }
            onChange(val)
        },
        [onChange, setV, value]
    )

    const handleItemClick = useAtomCallback(
        useCallback(
            (get, set, sizeOrUnit: RectSizeOption) => {
                const selectedContext = get(selectedBlockContextAtom)
                const index = get(pageBlockContextIndexAtom)
                const block = selectedContext?.block
                const parentBlock =
                    selectedContext?.parentUniqueKey && index ? index.contextMap.get(selectedContext.parentUniqueKey)?.block : undefined
                const t = lowerCaseType === 'width' ? 'width' : 'height'
                const lbl = lowerCaseType === 'width' ? '宽' : '高'
                switch (sizeOrUnit) {
                    case 'auto': {
                        const val: SizeInputValue = { size: sizeOrUnit }
                        if (!block) {
                            setV(val)
                            if (isDeepEqual(value, val)) {
                                return
                            }
                            onChange(val)
                            setDropdownOpened(false)
                            return
                        }
                        const children = getBlockChildren(block)
                        if (children) {
                            const isHasFill = children.some(child => child.config.breakPoint.size[t].size === 'fill')
                            if (isHasFill) {
                                Toast.error(`当存在子组件${lbl}为撑满容器时，无法设置为适应内容`)
                                return
                            }
                        }
                        setV(val)
                        if (isDeepEqual(value, val)) {
                            return
                        }
                        onChange(val)
                        setDropdownOpened(false)
                        return
                    }
                    case 'fill': {
                        const val: SizeInputValue = { size: sizeOrUnit }
                        if (!parentBlock) {
                            if (t === 'height') {
                                Toast.error(`当页面${lbl}为适应内容时，无法设置为撑满容器`)
                                return
                            }
                            setV(val)
                            if (isDeepEqual(value, val)) {
                                return
                            }
                            onChange(val)
                            setDropdownOpened(false)
                            return
                        }
                        const isHasAuto = parentBlock.config.breakPoint.size[t].size === 'auto'
                        if (isHasAuto) {
                            Toast.error(`当父组件${lbl}为适应内容时，无法设置为撑满容器`)
                            return
                        }
                        setV(val)
                        if (isDeepEqual(value, val)) {
                            return
                        }
                        onChange(val)

                        break
                    }
                    case 'px':
                    case 'rem':
                    case '%':
                    case 'vw':
                    case 'vh': {
                        if (!block || !selectedContext) {
                            break
                        }
                        const nodeIdWithScope = { id: block.id, scope: selectedContext.scope }
                        const size = convertSize(nodeIdWithScope, type, sizeOrUnit, previewType, parentBlock) as RectSize
                        // 仅当绑定的是真正的变量引用时保留绑定；VALUE 字面量（来自输入/拖拽）需按新单位重算
                        const currentVariable = v.variable
                        const keepBinding = currentVariable && currentVariable.type !== VariableType.VALUE
                        const val: SizeInputValue = keepBinding
                            ? { size: undefined, unit: sizeOrUnit, variable: currentVariable }
                            : { size, unit: sizeOrUnit }
                        setV(val)
                        if (isDeepEqual(value, val)) {
                            return
                        }
                        onChange(val)
                        break
                    }

                    default: {
                        break
                    }
                }
                setDropdownOpened(false)
            },
            [lowerCaseType, setV, value, onChange, type, previewType, v.variable]
        )
    )

    const handleBlur = useCallback(
        (ev: React.FocusEvent<HTMLInputElement>) => {
            const v = ev.target.value
            if (v === '适应内容' || v === '撑满容器') {
                const size = rectSizeValue[v]
                const val: SizeInputValue = { size }
                if (isDeepEqual(val, value)) {
                    return
                }
                onChange(val)
                return
            }
            const number = transform2Number(v)
            const validNumber = number > 0 ? number : 0
            const limitNumber = transform2Number(validNumber.toFixed(2))
            const val: SizeInputValue = { size: limitNumber, unit: value?.unit || 'px' }
            if (isDeepEqual(val, value)) {
                return
            }
            onChange(val)
        },
        [onChange, value]
    )

    // 拖拽手柄：fill/auto 与数值态共用，disabled 时仅显示图标不可拖拽
    const sizeIconNode = inputIconType ? (
        <SizeIcon
            type={inputIconType}
            disable={disabled}
            min={0}
            value={currentSizeNumber}
            blockStyleSize={sizeIconBlockStyleSize}
            onChangeMove={val => setDragStartedReadOnly(val && (v?.size === 'auto' || v?.size === 'fill'))}
            onChange={handleMoveChange}
        />
    ) : undefined

    // 拖拽从 auto/fill 起始时，拖拽期间保持 readOnly 分支，避免 SizeIcon 卸载中断拖拽
    const showReadOnly = isReadOnly || dragStartedReadOnly

    return (
        <SCxRectSizeInputWrapper>
            {showReadOnly ? (
                <Input
                    focusOutLine={false}
                    readOnly
                    value={
                        dragStartedReadOnly
                            ? String(currentSizeNumber ?? '')
                            : v.size === 'auto'
                              ? '适应内容'
                              : '撑满容器'
                    }
                    prefix={sizeIconNode}
                    radius={0}
                    disabled={disabled}
                    type="text"
                    styles={{
                        wrapper: {
                            borderTopRightRadius: '0!important',
                            borderBottomRightRadius: '0!important'
                        }
                    }}
                    onChange={() => {}}
                    onBlur={handleBlur}
                />
            ) : (
                <SCxVariableSelectWrapper>
                    <VariableSelect
                        field={emptyNumberField}
                        value={variableValue}
                        innerTypes={getInputInnerTypeMatch('NUMBER')}
                        enableFormula
                        onChange={handleVariableChange}
                        viewOptions={variableOptions?.viewOptions}
                        pageOptions={variableOptions?.pageOptions}
                        userOption={variableOptions?.userOption}
                        dataSourceList={variableOptions?.dataSourceList}
                        disabled={disabled}
                        width="100%"
                        prefix={sizeIconNode}
                        wrapperStyle={{
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0
                        }}
                        focusOutLine={false}
                        style={{
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0
                        }}
                    />
                </SCxVariableSelectWrapper>
            )}
            <Popover opened={dropdownOpened} width={180} position="bottom-end" disabled={disabled} onChange={setDropdownOpened}>
                <Popover.Target>
                    <SCxDropdownButton>
                        <IconFont type="ArrowDownSmall" color="var(--color-gray-400)" />
                    </SCxDropdownButton>
                </Popover.Target>
                <Popover.Dropdown>
                    <SelectDropdown
                        options={dropdownOptions}
                        onSelect={v => handleItemClick(v as RectSizeOption)}
                        itemExtra={option =>
                            (value?.unit === option.value || value?.size === option.value) && (
                                <IconFont type="Tick" color="var(--color-main)" />
                            )
                        }
                    />
                </Popover.Dropdown>
            </Popover>
        </SCxRectSizeInputWrapper>
    )
}
